import { createHelpBlock } from "../utils/_form-utils";

export interface HTMLElementWithOTC extends HTMLElement {
  __otcInstance: OneTimeCode;
}

export class OneTimeCode {
  private readonly container: HTMLElement;
  private readonly inputsContainer: HTMLElement;
  private readonly form: HTMLFormElement;
  private readonly hiddenInput: HTMLInputElement;
  private readonly inputs: HTMLInputElement[];
  private emptyChar: string = "";

  constructor(inputsContainer: HTMLElement | string, hiddenInput: HTMLInputElement | string) {
    this.inputsContainer = this.resolveElement<HTMLElement>(inputsContainer);
    this.hiddenInput = this.resolveElement<HTMLInputElement>(hiddenInput);

    const container = this.hiddenInput.closest<HTMLElement>(".one-time-code");
    if (container === null) {
      throw new Error("One time code root element not found");
    }
    this.container = container;

    const form = this.hiddenInput.closest<HTMLFormElement>("form");
    if (form === null) {
      throw new Error("One time code form not found");
    }
    this.form = form;

    this.inputs = [...(this.inputsContainer.querySelectorAll<HTMLInputElement>("input[type=text], input[type=number], input[type=password]"))];

    this.init();

    (this.container as HTMLElementWithOTC).__otcInstance = this;
  }

  public getValue(): string {
    return this.inputs.map(input => input.value || this.emptyChar).join("");
  }

  public setValue(value: string): void {
    const chars = value.split("");
    for (let i = 0; i < this.inputs.length; i++) {
      this.setCharInputValue(i, chars[i] || "");
    }
  }

  public resetValue(): void {
    for (let i = 0; i < this.inputs.length; i++) {
      this.setCharInputValue(i, "");
    }
  }

  public reset(): void {
    this.resetValue();
    this.enableCharInputs();
    this.hideError();
    this.removeSuccessClass();
  }

  private resolveElement<T extends HTMLElement>(ref: T | string): T {
    if (typeof ref === "string") {
      const element = document.querySelector<T>(ref);

      if (!element) {
        throw new Error(`Element "${ref}" not found`);
      }

      return element;
    }

    return ref;
  }

  private init(): void {
    this.inputs.forEach((input, index) => {
      input.addEventListener("input", (e: Event) => {
        if (e instanceof InputEvent) {
          this.handleCharInputInput(e, index);
        }
      });
      input.addEventListener("keydown", (e: KeyboardEvent) => this.handleCharInputKeydown(e, index));
    });
  }

  private handleCharInputInput(e: InputEvent, index: number): void {
    this.hideError();

    const value = e.target instanceof HTMLInputElement ? e.target.value : (e.data || "");

    const chars = value.split("");
    chars.forEach((char, i) => {
      if (index + i < this.inputs.length) {
        this.setCharInputValue(index + i, char);
      }
    });

    const isCodeFull = this.getValue().length === this.inputs.length;
    if (!isCodeFull) {
      const nextIndex = Math.min(this.inputs.length - 1, index + chars.length);
      this.inputs[nextIndex].focus();
    } else {
      this.submitValue();
    }
  }

  private handleCharInputKeydown(e: KeyboardEvent, index: number): void {
    const input = this.inputs[index];

    switch (e.key) {
      case "Backspace":
        if (index === 0) {
          this.setCharInputValue(index, "");
        } else if (input.value === "" && index > 0) {
          this.setCharInputValue(index - 1, "");
          this.inputs[index - 1].focus();
        }
        break;
      case "Delete":
        if (index < this.inputs.length - 1) {
          for (let i = index; i < this.inputs.length - 1; i++) {
            this.setCharInputValue(i, this.inputs[i + 1].value);
          }
          this.setCharInputValue(this.inputs.length - 1, "");
          e.preventDefault();
        }
        break;
      case "ArrowLeft":
        if (input.selectionStart === 0 && index > 0) {
          this.inputs[index - 1].focus();
          this.inputs[index - 1].select();
          e.preventDefault();
        }
        break;
      case "ArrowRight":
        if (
          input.selectionEnd === input.value.length &&
          index < this.inputs.length - 1
        ) {
          this.inputs[index + 1].focus();
          this.inputs[index + 1].select();
          e.preventDefault();
        }
        break;
    }
  }

  private setCharInputValue(index: number, value: string): void {
    if (!this.inputs[index]) {
      return;
    }
    this.inputs[index].value = value.substring(0, 1);

    this.updateValue();
  }

  private updateValue(): void {
    this.hiddenInput.value = this.getValue();
  }

  private submitValue(): void {
    this.disableCharInputs();

    this.form.dispatchEvent(new CustomEvent("OTCSubmit"));
  }

  public showSuccess() {
    this.addSuccessClass();
  }

  private addSuccessClass(): void {
    this.container.classList.add("success");
  }

  private removeSuccessClass(): void {
    this.container.classList.remove("success");
  }

  public showError(errors: string[]): void {
    this.appendErrorMessage(errors);
    this.enableCharInputs();
    this.resetValue();
    this.focusFirstInput();
  }

  private hideError(): void {
    this.container.querySelector(".help-block")?.remove();
    this.container.classList.remove("has-error");
  }

  private appendErrorMessage(errors: string[]) {
    const helpBlock = createHelpBlock(errors);
    this.container.classList.add("has-error");
    this.container.append(helpBlock);
  }

  private enableCharInputs(): void {
    for (const input of this.inputs) {
      input.disabled = false;
    }
  }

  private disableCharInputs(): void {
    for (const input of this.inputs) {
      input.disabled = true;
    }
  }

  public getInvalidMessage(): string {
    return this.container.dataset.invalidMessage ?? "Invalid code";
  }

  public getExpiredMessage(): string {
    return this.container.dataset.expiredMessage ?? "Expired code";
  }

  public getAttemptsLimitExceededMessage(): string {
    return this.container.dataset.attemptsLimitExceededMessage ?? "The limit of attempts is exceeded";
  }

  public focusFirstInput(): void {
    this.inputs[0].focus();
  }
}